import { create } from 'zustand';
import type { User, Conversation, Message } from '../types';

interface AppState {
  // Auth
  user: User | null;
  isAuthenticated: boolean;
  setUser: (user: User | null) => void;

  // Conversations
  conversations: Conversation[];
  selectedConversation: Conversation | null;
  setConversations: (conversations: Conversation[]) => void;
  setSelectedConversation: (conversation: Conversation | null) => void;
  updateConversation: (id: string, updates: Partial<Conversation>) => void;
  addConversation: (conversation: Conversation) => void;

  // Messages
  messages: Record<string, Message[]>;
  setMessages: (conversationId: string, messages: Message[]) => void;
  addMessage: (conversationId: string, message: Message) => void;

  // Typing indicators
  typingUsers: Record<string, { name: string; isTyping: boolean }>;
  setTypingUser: (conversationId: string, name: string, isTyping: boolean) => void;

  // AI Suggestions
  aiSuggestions: Record<string, { answer: string; confidence: number; sources: any[] }>;
  setAISuggestion: (conversationId: string, suggestion: any) => void;
  clearAISuggestion: (conversationId: string) => void;

  // UI State
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  mobileConversationView: boolean;
  setMobileConversationView: (show: boolean) => void;

  // Filters
  statusFilter: string | null;
  handlingModeFilter: string | null;
  searchQuery: string;
  setStatusFilter: (status: string | null) => void;
  setHandlingModeFilter: (mode: string | null) => void;
  setSearchQuery: (query: string) => void;
}

export const useStore = create<AppState>((set) => ({
  // Auth
  user: null,
  isAuthenticated: false,
  setUser: (user) => set({ user, isAuthenticated: !!user }),

  // Conversations
  conversations: [],
  selectedConversation: null,
  setConversations: (conversations) => set({ conversations }),
  setSelectedConversation: (conversation) => set({ selectedConversation: conversation }),
  updateConversation: (id, updates) =>
    set((state) => ({
      conversations: state.conversations.map((conv) =>
        conv.id === id ? { ...conv, ...updates } : conv
      ),
      selectedConversation:
        state.selectedConversation?.id === id
          ? { ...state.selectedConversation, ...updates }
          : state.selectedConversation,
    })),
  addConversation: (conversation) =>
    set((state) => ({
      conversations: [conversation, ...state.conversations],
    })),

  // Messages
  messages: {},
  setMessages: (conversationId, messages) =>
    set((state) => ({
      messages: { ...state.messages, [conversationId]: messages },
    })),
  addMessage: (conversationId, message) =>
    set((state) => ({
      messages: {
        ...state.messages,
        [conversationId]: [...(state.messages[conversationId] || []), message],
      },
    })),

  // Typing indicators
  typingUsers: {},
  setTypingUser: (conversationId, name, isTyping) =>
    set((state) => ({
      typingUsers: {
        ...state.typingUsers,
        [conversationId]: { name, isTyping },
      },
    })),

  // AI Suggestions
  aiSuggestions: {},
  setAISuggestion: (conversationId, suggestion) =>
    set((state) => ({
      aiSuggestions: {
        ...state.aiSuggestions,
        [conversationId]: suggestion,
      },
    })),
  clearAISuggestion: (conversationId) =>
    set((state) => {
      const newSuggestions = { ...state.aiSuggestions };
      delete newSuggestions[conversationId];
      return { aiSuggestions: newSuggestions };
    }),

  // UI State
  sidebarOpen: true,
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  mobileConversationView: false,
  setMobileConversationView: (show) => set({ mobileConversationView: show }),

  // Filters
  statusFilter: null,
  handlingModeFilter: null,
  searchQuery: '',
  setStatusFilter: (status) => set({ statusFilter: status }),
  setHandlingModeFilter: (mode) => set({ handlingModeFilter: mode }),
  setSearchQuery: (query) => set({ searchQuery: query }),
}));
